import { FC, useState } from 'react';

import { MultiValue, StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';

import { IWalletTag, useGetWalletTagsQuery } from '@entities/wallet-tags';
import { reactFormSelectMultiStyles } from '@shared/components/Form';

export interface ITagOption {
  value: string;
  label: string;
  color?: string | null;
  /** Set by react-select's creatable when the option was typed, not picked from the list. */
  __isNew__?: boolean;
}

interface IProps {
  /** Tags already on the wallet (Edit flow) — pre-selected as chips. */
  defaultTags?: IWalletTag[];
  onChange: (selected: ITagOption[]) => void;
  isDisabled?: boolean;
}

export const tagToOption = (tag: IWalletTag): ITagOption => ({
  value: String(tag.id),
  label: tag.name,
  color: tag.color,
});

const tagStyles: StylesConfig<ITagOption, true> = {
  ...(reactFormSelectMultiStyles as unknown as StylesConfig<ITagOption, true>),
  multiValue: (base, state) => ({
    ...base,
    borderRadius: 'var(--radius)',
    backgroundColor: state.data.color
      ? `color-mix(in srgb, ${state.data.color} 35%, var(--input-bg))`
      : base.backgroundColor,
  }),
};

export const WalletTagsSelect: FC<IProps> = ({ defaultTags = [], onChange, isDisabled = false }) => {
  const { data, isLoading } = useGetWalletTagsQuery();

  const options: ITagOption[] = (data?.data ?? []).map(tagToOption);
  const [selected, setSelected] = useState<MultiValue<ITagOption>>(defaultTags.map(tagToOption));

  const handleChange = (next: MultiValue<ITagOption>) => {
    setSelected(next);
    onChange(next as ITagOption[]);
  };

  // Names that already exist (in the list or already picked) — don't offer "Create" for these.
  const existingLabels = new Set(
    [...options, ...selected].map(option => option.label.toLowerCase()),
  );

  return (
    <CreatableSelect
      isMulti
      isClearable
      isLoading={isLoading}
      isDisabled={isDisabled}
      options={options}
      value={selected}
      onChange={handleChange}
      styles={tagStyles}
      classNamePrefix='select'
      maxMenuHeight={200}
      menuPosition='fixed'
      placeholder='Select or create tags…'
      formatCreateLabel={input => `Create "${input}"`}
      // Don't offer "Create" for a name that already exists (BE normalises to lower-case, names are unique).
      isValidNewOption={inputValue => {
        const value = inputValue.trim().toLowerCase();
        return !!value && !existingLabels.has(value);
      }}
    />
  );
};
